var Shopify = Shopify ?? {};

Shopify.URL = {
  withSearchParams: function (_url, params) {
    const url = new URL(_url);
    Object.keys(params).forEach(key => {
      if (params[key] === null || params[key] === "null") url.searchParams.delete(key);
      else url.searchParams.set(key, params[key]);
    });  
    return url.toString(); 
  }
}

Shopify.uscape = (obj, decode = true) => {
  const strings = new Map()
  let decoder
  const visit = (value) => {
    if (typeof value === 'string') {
      if (!/[&%]/.test(value)) return value
      if (strings.has(value)) return strings.get(value)
      decoder ||= document.createElement('textarea')
      decoder.innerHTML = value
      const result = decode
        ? decodeURIComponent(decoder.value.replaceAll('&amp;quot;', '&quot;').replaceAll('&quot;', '"').replaceAll('&#39;', "'").replaceAll('%2F', '/').replaceAll('%', '%25'))
        : decoder.value
      strings.set(value, result)
      return result
    }
    if (Array.isArray(value)) return value.map(visit)
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, visit(entry)]))
    }
    return value
  }
  return visit(obj)
}

Shopify.slugify = (str, replacement ="-") => (str ?? '').trim().toLowerCase().replaceAll(" ", replacement).replaceAll("/", `${replacement}${replacement}`);

Shopify.Image = (image, width, height, _crop = null) => {
  if (!image) return null;
  const options = {}
  const crop = _crop ?? (width && height ? "center" : null);
  if (width) options.width = width
  if (height) options.height = height
  if (crop) options.crop = crop
  if (image.includes('cdn.accentuate.io/') && (width || height)) {
    const url = new URL(image.startsWith('http') ? image : 'https:' + image)
    const dimensions = [width && Math.round(width), height && Math.round(height)]
    const transform = []
    if (width && height && crop) transform.push(`cover=${dimensions[0]}:${dimensions[1]}`)
    transform.push(`resize=${dimensions[0] || '-'}${height ? 'x' + dimensions[1] : ''}`)
    url.searchParams.set('transform', transform.join('/'))
    return url.toString()
  }
  return Shopify.URL.withSearchParams(`${image.startsWith('http') ? '' : 'https:'}${image}`, options)
}

Shopify.ImageSrcset = (image) => [360, 540, 720, 1080, 1440, 2160]
  .map(width => `${Shopify.Image(image, width)} ${width}w`).join(', ')

Shopify.ProductImageSizes = (image, multiple = false, thumbnails = true) => {
  const ratio = image?.height > 0 ? image.width / image.height : 1
  const width = thumbnails ? (multiple ? 0.7647 * ratio : Math.min(1, 0.7647 * ratio)) : 1
  return `(max-width: 989px) ${width * 100}vw, ${width * 64}vw`
}

const imageAltDataCache = new Map()
Shopify.ImageAltData = (alt, options = []) => {
  const cacheKey = JSON.stringify([alt, options.map(option => option.name)])
  if (imageAltDataCache.has(cacheKey)) return imageAltDataCache.get(cacheKey)
  const text = (typeof alt === 'string' ? alt : '')
    .replace(/&(?:amp;)?(?:quot|#34|#x22);/gi, '"')
    .replace(/&(?:amp;)?(?:apos|#39|#x27);/gi, "'")
  const objects = []
  let hasFailedParse = false

  // Read each complete JSON object separately. Commas and braces inside text belong to the text.
  for (let start = 0; start < text.length; start++) {
    if (text[start] !== '{' && text[start] !== '[') continue
    const isObject = text[start] === '{'
    const closings = []
    let quote = null
    let escaped = false
    let source = ''
    let complete = false
    let end = start
    for (; end < text.length; end++) {
      const character = text[end]
      if (quote) {
        if (escaped) {
          source += character
          escaped = false
        } else if (character === '\\') {
          source += character
          escaped = true
        } else if (character === quote) {
          source += quote === '’' || quote === '”' ? '"' : character
          quote = null
        } else {
          source += (quote === '’' || quote === '”') && character === '"' ? '\\"' : character
        }
        continue
      }
      if (character === '"' || character === "'" || character === '‘' || character === '“') {
        quote = character === '‘' ? '’' : character === '“' ? '”' : character
        source += character === '‘' || character === '“' ? '"' : character
        continue
      }
      source += character
      if (character === '{') closings.push('}')
      else if (character === '[') closings.push(']')
      else if (character === '}' || character === ']') {
        if (closings.pop() !== character) break
        if (!closings.length) {
          complete = true
          break
        }
      }
    }
    start = end
    let parsed = false
    if (complete) {
      const legacySource = options.reduce((value, { name }) => value.replace(name, Shopify.slugify(name, '_')), source)
      const parsers = [
        value => (typeof JSON5 !== 'undefined' ? JSON5 : JSON).parse(value),
        value => JSON.parse(value.replaceAll("'", '"')),
        () => Shopify.parseWrongJSON(legacySource),
        () => Shopify.parseWrongJSON(legacySource.replaceAll("'", '"'))
      ]
      for (const parse of parsers) {
        try {
          const metadata = parse(source)
          if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) objects.push(metadata)
          parsed = true
          break
        } catch (_) {}
      }
    }
    if (!parsed && isObject) hasFailedParse = true
  }
  const result = { objects, hasFailedParse }
  if (imageAltDataCache.size >= 1000) imageAltDataCache.delete(imageAltDataCache.keys().next().value)
  imageAltDataCache.set(cacheKey, result)
  return result
}

Shopify.ImagesByVariant = (images, product, variant, bundle = null) => images.filter(image => {
  if (variant.id && image.attachedToVariant && !(image.variantsIds ?? []).includes(variant.id)) return false

  const { objects, hasFailedParse } = Shopify.ImageAltData(image.alt, product.options)
  const imageData = objects.reduce((data, metadata) => {
    const normalized = { ...metadata }
    product.options.forEach(({ name }) => {
      if (Object.prototype.hasOwnProperty.call(metadata, name)) normalized[Shopify.slugify(name, '_')] = metadata[name]
    })
    return { ...data, ...normalized }
  }, {})

  if (hasFailedParse && !Object.keys(imageData).length) return false;
  if (!Object.keys(imageData).length) return true;

  const optionsWithValue = product.options.map((option, i) => ({
    ...{},
    ...option,
    slug: Shopify.slugify(option.name, "_"),
    value: variant.options[i]
  }))

  let altObjectBelongsToVariant = false;
  let altObjectContainsOptions = false;

  optionsWithValue.forEach(option => {
    if(imageData[option.slug]) altObjectContainsOptions = true;
  });

  if (altObjectContainsOptions){
    altObjectBelongsToVariant = true;
    optionsWithValue.forEach(option => {
      if (!altObjectBelongsToVariant) return;
      // Reset to false if exists
      if(imageData[option.slug]) altObjectBelongsToVariant = false;
      // Set to true if value is the same
      if(imageData[option.slug] === option.value) altObjectBelongsToVariant = true;
    })
  }

  
  if (!(!altObjectContainsOptions || altObjectBelongsToVariant)) return false;

  if (imageData.tag && bundle && imageData.tag !== bundle.id) return false;

  return true;
})

Shopify.Money = function(_cents, f) {
  let value = '';
  const format = f ?? window.__FORMAT_MONEY__ ?? "${{amount}}";
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = (format || this.money_format);
  const nonAbsCents = parseInt((typeof _cents === 'string' ? _cents.replace('.','') : _cents));
  const cents = Math.abs(nonAbsCents);

  function defaultOption(opt, def) {
     return (typeof opt == 'undefined' ? def : opt);
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal   = defaultOption(decimal, '.');

    if (isNaN(number) || number == null) { return 0; }

    number = (number/100.0).toFixed(precision);

    const parts   = number.split('.'),
        dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
        cents   = parts[1] ? (decimal + parts[1]) : '';

    return dollars + cents;
  }

  switch(formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return `${nonAbsCents < 0 ? '-': ''}${formatString.replace(placeholderRegex, value)}`;
};


Shopify.Array = {
  /**
 * Returns values contained in both arrays, A and B (intersection)
 *
 * @param {Array} A Array A
 * @param {Array} B Array B
 *
 * @return {Array} resulting array
 *
 * For [1,2,3] [2,3] it will yield [2,3]. On the other hand, for [1,2,3] [2,3,5] will return the same thing.
 */
intersection: (A, B) => A.filter((x) => B.includes(x)),

/**
 * Returns values from A that are not in B
 *
 * @param {Array} A Array A
 * @param {Array} B Array B
 *
 * @return {Array} resulting array
 *
 * For [1,2,3] [2,3] it will yield [1]. On the other hand, for [1,2,3] [2,3,5] will return the same thing.
 */
difference: (A, B) => A.filter((x) => !B.includes(x)),

/**
 * Returns values from A and B that are only in one of the two arrays (don't belong to intersection)
 *
 * @param {Array} A Array A
 * @param {Array} B Array B
 *
 * @return {Array} resulting array
 *
 */
symetricDifference: (A, B) =>
  A.filter((x) => !B.includes(x)).concat(B.filter((x) => !A.includes(x))),

/**
 * Returns an array of unique elements
 */
unique: (a) =>
  a.filter((value, index, self) => self.indexOf(value) === index)
}


Shopify.uuidv4 = () => ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
(c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
);

Shopify.uuid = () => `${Shopify.uuidv4()}-${Date.now()}`

Shopify.loadStyles = (src, id = null) => {
  if (id && document.getElementById(id)) return
    {
        var head  = document.getElementsByTagName('head')[0];
        var link  = document.createElement('link');
        link.id   = id;
        link.rel  = 'stylesheet';
        link.type = 'text/css';
        link.href = src;
        link.media = 'all';
        head.appendChild(link);
    }
}

Shopify.loadScript = (src, callback) => {
  var s,
      r,
      t;
  r = false;
  s = document.createElement('script');
  s.type = 'text/javascript';
  s.src = src;
  s.onload = s.onreadystatechange = function() {
    if ( !r && (!this.readyState || this.readyState == 'complete') )
    {
      r = true;
      callback();
    }
  };
  t = document.getElementsByTagName('script')[0];
  t.parentNode.insertBefore(s, t);
}

Shopify.parseWrongJSON = (json) => Shopify.uscape(JSON.parse(json.replace(/(['"])?([a-z0-9A-Z_]+)(['"])?:/g, '"$2": ')));

Shopify.IsLocalEnv = window.location.host.includes("127.0.0.1")

Shopify.PurgeCache = Shopify.IsLocalEnv || window.location.search.includes("purge")

Shopify.SessionStorage = {
  prefix: "floyd_",

  set: (k, value, expire = 3600) => {
    const key = `${Shopify.SessionStorage.prefix}${k}`
    if (Shopify.PurgeCache || !window.sessionStorage) return;
    window.sessionStorage.setItem(key, JSON.stringify({
      expire: expire ? Date.now() + expire * 1000 : false,
      value
    }))
    return
  }, 

  get: (k) => {
    const key = `${Shopify.SessionStorage.prefix}${k}`
    if (Shopify.PurgeCache || !window.sessionStorage || !window.sessionStorage.getItem(key)) return;
    item = JSON.parse(sessionStorage.getItem(key));
    if (item.expire && item.expire < Date.now()) {
      window.sessionStorage.removeItem(key)
      return;
    }
    return item.value;
  },

  remove: (k) => {
    if (!window.sessionStorage) return;
    const key = `${Shopify.SessionStorage.prefix}${k}`
    window.sessionStorage.removeItem(key)
  }
};

Shopify.isDeepEqual = (a, b, flag) => {
  if (a === b) return true;

  if (a && b && typeof a == 'object' && typeof b == 'object') {
    if (a.constructor !== b.constructor) return false;

    var length, i, keys;
    if (Array.isArray(a)) {
      length = a.length;
      if (length != b.length) return false;
      for (i = length; i-- !== 0;)
        if (!Shopify.isDeepEqual(a[i], b[i], flag)) return false;
      return true;
    }


    if ((a instanceof Map) && (b instanceof Map)) {
      if (a.size !== b.size) return false;
      for (i of a.entries())
        if (!b.has(i[0])) return false;
      for (i of a.entries())
        if (!Shopify.isDeepEqual(i[1], b.get(i[0]), flag)) return false;
      return true;
    }

    if ((a instanceof Set) && (b instanceof Set)) {
      if (a.size !== b.size) return false;
      for (i of a.entries())
        if (!b.has(i[0])) return false;
      return true;
    }

    if (ArrayBuffer.isView(a) && ArrayBuffer.isView(b)) {
      length = a.length;
      if (length != b.length) return false;
      for (i = length; i-- !== 0;)
        if (a[i] !== b[i]) return false;
      return true;
    }

    if (a.constructor === RegExp) return a.source === b.source && a.flags === b.flags;
    if (a.valueOf !== Object.prototype.valueOf) return a.valueOf() === b.valueOf();
    if (a.toString !== Object.prototype.toString) return a.toString() === b.toString();

    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) return false;

    for (i = length; i-- !== 0;)
      if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;

    for (i = length; i-- !== 0;) {
      var key = keys[i];
      if (!Shopify.isDeepEqual(a[key], b[key], flag)) return false;
    }

    return true;
  }

  // true if both NaN, false otherwise
  return a!==a && b!==b;
};

Shopify.cloneDeep = (entity, cache = new WeakMap) => {
  const cloneDeep = Shopify.cloneDeep;
  const referenceTypes = ['Array', 'Object', 'Map', 'Set', 'Date'];
  const entityType = Object.prototype.toString.call(entity);
  if (
    !new RegExp(referenceTypes.join('|')).test(entityType) ||
    entity instanceof WeakMap ||
    entity instanceof WeakSet
  ) return entity;
  if (cache.has(entity)) {
    return cache.get(entity);
  }
  const c = new entity.constructor;
  
  if (entity instanceof Map) {
    entity.forEach((value, key) => c.set(cloneDeep(key), cloneDeep(value)));
  }
  if (entity instanceof Set) {
    entity.forEach((value) => c.add(cloneDeep(value)));
  }
  if (entity instanceof Date) {
    return new Date(entity);
  }
  cache.set(entity, c);
  return Object.assign(c, ...Object.keys(entity).map((prop) => ({ [prop]: cloneDeep(entity[prop], cache) })));
}
